import { adaptiveEinmaleinsConfigSchema, type RecommendationFrequency } from "@calega/content";
import { recommendationRhythm } from "./RecommendationPlanFields";
import { useEffect, useState } from "react";
import type { Account } from "../lib/types";
import { api } from "../lib/api";
import { ContentPageView } from "./ContentPage";

interface AdaptiveAssignmentViewData {
  id: string;
  title: string;
  message: string;
  status: "ACTIVE" | "PAUSED" | "COMPLETED";
  contentSlug: string;
  contentTitle: string;
  exerciseType: string;
  config: unknown;
  frequency: RecommendationFrequency | null;
  weekStartsOn: number | null;
  recommendedMinutes: number | null;
  featureStats?: Array<{ feature: string; attempts: number; errors: number; errorRate: number }>;
}

const featureLabels: Record<string, string> = {
  addition_without_tens_transition: "Addition ohne Zehnerübergang",
  addition_with_tens_transition: "Addition mit Zehnerübergang",
  subtraction_without_tens_transition: "Subtraktion ohne Zehnerübergang",
  subtraction_with_tens_transition: "Subtraktion mit Zehnerübergang",
  multiplication: "Multiplikation",
  division: "Division",
};

export function AdaptiveAssignmentView({ account, assignmentId }: { account: Account; assignmentId: string }) {
  const [assignment, setAssignment] = useState<AdaptiveAssignmentViewData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setAssignment(null);
    api<AdaptiveAssignmentViewData>(`/learning/adaptive-assignments/${encodeURIComponent(assignmentId)}`)
      .then(setAssignment)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Der Übungsauftrag konnte nicht geladen werden."));
  }, [assignmentId]);

  if (error) return <div className="alert error">{error}</div>;
  if (!assignment) return <div className="loading-card">Übungsauftrag wird geladen …</div>;
  const einmaleins = assignment.exerciseType === "einmaleins" ? adaptiveEinmaleinsConfigSchema.safeParse(assignment.config) : null;
  return <div className="adaptive-assignment-page">
    <header className="dashboard-card">
      <p className="eyebrow">Adaptive Empfehlung</p>
      <h1>{assignment.title}</h1>
      {einmaleins?.success && <p>{einmaleins.data.trainingMode === "core" ? "Kernaufgaben: 1er-, 2er-, 5er- und 10er-Reihe sowie Quadrataufgaben bis 10 × 10." : `Reihen üben: ${einmaleins.data.rows.map((row) => `${row}er`).join(", ")}.`}</p>}
      {assignment.message && <p className="recommendation-instruction">{assignment.message}</p>}
      <p>{recommendationRhythm(assignment.frequency, assignment.weekStartsOn)}{assignment.recommendedMinutes != null ? ` · ${assignment.recommendedMinutes} Minuten pro Durchlauf (zur Orientierung)` : ""}</p>
      {assignment.featureStats && assignment.featureStats.length > 0 && <div className="analytics-grid">{assignment.featureStats.map((stat) => <div className="metric-card" key={stat.feature}><strong>{featureLabels[stat.feature] ?? "Rechenformat"}</strong><span>{stat.attempts} Versuche</span><b>{Math.round(stat.errorRate * 100)} % Fehler</b></div>)}</div>}
      {assignment.status !== "ACTIVE" && <div className="alert info">Dieser Auftrag ist derzeit {assignment.status === "PAUSED" ? "pausiert" : "abgeschlossen"}.</div>}
    </header>
    {assignment.status === "ACTIVE" ? <ContentPageView account={account} slug={assignment.contentSlug} adaptiveAssignmentId={assignment.id} /> : <div className="dashboard-card"><p>Für diesen Auftrag ist kein neuer Durchgang freigeschaltet.</p></div>}
  </div>;
}
